import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import { Reveal, Stagger, staggerItem } from "@/components/Reveal";
import { motion } from "framer-motion";
import CTASection from "@/components/CTASection";
import { useSeo } from "@/lib/seo";
import { useBrand } from "@/context/SiteContext";
import { VALUES, brandify } from "@/data/content";

const SEGMENTS = ["Solar Inverters", "UPS Systems", "LFP Batteries", "Energy Storage Systems", "EV Charging", "Solar Modules", "Solar BOS", "Solar & UPS EPC"];

export default function About() {
  const brand = useBrand();
  useSeo({ title: `About Us | ${brand.name}`, description: brand.description, canonical: `https://${brand.domain}/about` });
  return (
    <div>
      <PageHeader label={`About ${brand.name}`} title="Reliable, Integrated & Future-Ready Energy Solutions" sub={brand.description} image="https://images.unsplash.com/photo-1786913508115-51748da1339b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600" />
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 space-y-4 text-slate-300 leading-relaxed">
        <Reveal><p>{brandify("Hindustan Solar Power is a renewable energy and power solutions company focused on delivering reliable products and integrated solutions for solar power generation, energy storage and power backup.", brand.name)}</p></Reveal>
        <Reveal><p>With a strong focus on solar inverters — particularly hybrid, on-grid and off-grid systems — we help customers and business partners select suitable technologies for their specific energy requirements.</p></Reveal>
        <Reveal><p>Our portfolio extends across solar inverters, UPS systems, LFP batteries, Energy Storage Systems, EV charging solutions, solar modules and complete Solar BOS. In addition to product supply, we undertake complete Solar and UPS EPC projects and support government, institutional, dealer and EPC requirements.</p></Reveal>
        <Reveal><p>Our objective is simple: make reliable, efficient and future-ready energy solutions accessible to every customer we serve.</p></Reveal>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16 grid md:grid-cols-2 gap-6">
        <Reveal className="card-surface p-8"><div className="mono-label">Our Vision</div><p className="text-slate-200 mt-3 text-lg leading-relaxed">To become a trusted and technology-driven energy solutions partner, enabling homes, businesses and institutions to transition towards cleaner, smarter and more reliable power.</p></Reveal>
        <Reveal delay={0.1} className="card-surface p-8"><div className="mono-label">Our Mission</div><p className="text-slate-200 mt-3 text-lg leading-relaxed">To provide dependable renewable energy and power solutions through quality products, technical expertise, responsible service and long-term customer relationships.</p></Reveal>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="font-display text-3xl font-bold text-white mb-8">Core Values</h2>
        <Stagger className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {VALUES.map((v) => (
            <motion.div key={v.title} variants={staggerItem} className="card-surface p-6">
              <div className="font-display font-semibold text-emerald-400 text-lg">{v.title}</div>
              <p className="text-sm text-slate-400 mt-2">{v.text}</p>
            </motion.div>
          ))}
        </Stagger>
      </section>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <h2 className="font-display text-3xl font-bold text-white mb-6">Business Segments</h2>
        <div className="flex flex-wrap gap-3">
          {SEGMENTS.map((s) => <span key={s} className="badge-terminal !text-sm">{s}</span>)}
        </div>
      </section>
      <CTASection />
    </div>
  );
}
